import React, { useEffect, useMemo, useState } from 'react';
import KnowledgeConsensus from './KnowledgeConsensus';
import RecommendationEngine from './RecommendationEngine';

// INTERNAL Learning Engine — Audee's institutional memory. Rendered as a tab
// inside Rate Admin (AdminDashboard), beside the Growth Dashboard. Not
// customer-facing. Every opted-in estimate becomes a Learning Job; the backend
// analyzer produces evidence-cited engineering OBSERVATIONS; a human approves
// them into engineering backlogs + a searchable knowledge base. Approving
// changes NOTHING in production — it only creates review records.
const API = 'https://tm-audit-platform-production.up.railway.app';

const fmt$ = (n) => '$' + Math.round(n || 0).toLocaleString();
const fmtNum = (n) => (n || 0).toLocaleString();
const fmtPct = (n) => (n == null ? '—' : Math.round(n * 100) + '%');
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

const STATUS_COLOR = {
    'Queued': 'var(--text-muted)', 'Analyzing': 'var(--blue)', 'Ready For Review': 'var(--amber)',
    'Approved': 'var(--green)', 'Rejected': 'var(--red, #c0392b)', 'Needs Investigation': 'var(--purple)',
    'Pending': 'var(--amber)', 'Open': 'var(--amber)', 'In Progress': 'var(--blue)', 'Done': 'var(--green)', 'Wont Do': 'var(--text-muted)',
    // Part 2 triage states + Part 2 closed-job rollup
    'Already Exists': 'var(--blue)', 'Duplicate': 'var(--text-muted)', 'False Alarm': 'var(--text-muted)', 'Reviewed': 'var(--blue)',
};
// Part 2: quick-review actions (Approve creates records; the rest are triage only).
const REVIEW_ACTIONS = [
    { action: 'Approve', cls: 'le-approve' }, { action: 'Reject', cls: 'le-reject' },
    { action: 'Needs Investigation', cls: 'le-investigate' }, { action: 'Already Exists', cls: 'le-triage' },
    { action: 'Duplicate', cls: 'le-triage' }, { action: 'False Alarm', cls: 'le-triage' },
];
// Part 7: false-positive subsystems (learning-only tags).
const FP_TYPES = ['False Positive', 'Parser issue', 'Rule issue', 'Knowledge issue', 'Carrier-specific issue', 'Recommendation issue'];
const PRI_COLOR = { high: 'var(--red, #c0392b)', medium: 'var(--amber)', low: 'var(--text-muted)' };
const CONF_COLOR = { high: 'var(--green)', medium: 'var(--amber)', low: 'var(--text-muted)' };

// Knowledge Triage — AI-assigned label colors. AI triages; humans approve.
const TRIAGE_COLOR = {
    'Already Known': 'var(--blue)',
    'Duplicate': 'var(--text-muted)',
    'New Candidate': 'var(--green)',
    'Needs Investigation': 'var(--purple)',
    'False Alarm / Low Signal': 'var(--text-muted)',
    'Supports Existing Recommendation': 'var(--blue)',
    'Conflicts With Existing Knowledge': 'var(--red, #c0392b)',
};
const TRIAGE_LABELS = Object.keys(TRIAGE_COLOR);

const BACKLOG_TYPES = [
    { key: 'parser', label: 'Parser' },
    { key: 'rule_engine', label: 'Rule Engine' },
    { key: 'carrier_intelligence', label: 'Carrier Intelligence' },
    { key: 'knowledge_base', label: 'Knowledge Base' },
    { key: 'regression', label: 'Regression' },
];
const KB_CATEGORIES = ['Water', 'Fire', 'Mold', 'Roofing', 'Contents', 'Commercial', 'Large Loss', 'Carrier Intelligence', 'Parser Improvements', 'False Positive Library', 'False Negative Library', 'Equipment', 'Documentation'];

function Badge({ text, color }) {
    return <span className="le-badge" style={{ color, borderColor: color }}>{text}</span>;
}

// Part 8: a compact "top recurring …" list (key + count), shown in a 3-up grid.
function TopList({ title, rows }) {
    const list = rows || [];
    return (
        <div className="le-top-list">
            <div className="le-top-title">{title}</div>
            {list.length === 0 && <div className="le-empty">No data yet.</div>}
            {list.map(r => (
                <div key={r.key} className="le-top-row"><span>{r.key}</span><b>{(r.count || 0).toLocaleString()}</b></div>
            ))}
        </div>
    );
}

function KpiCard({ label, value, accent, sub }) {
    return (
        <div className="gd-kpi">
            <div className="gd-kpi-label">{label}</div>
            <div className="gd-kpi-value" style={accent ? { color: accent } : undefined}>{value}</div>
            {sub && <div className="gd-kpi-sub">{sub}</div>}
        </div>
    );
}

export default function LearningEngine({ session }) {
    const [view, setView] = useState('dashboard');
    const [dashboard, setDashboard] = useState(null);
    const [queue, setQueue] = useState([]);
    const [backlog, setBacklog] = useState([]);
    const [knowledge, setKnowledge] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    // queue controls
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortKey, setSortKey] = useState('created_at');
    const [sortDir, setSortDir] = useState('desc');

    // estimate learning (Part 3) + analytics (Part 8)
    const [estimateJobs, setEstimateJobs] = useState([]);
    const [estimateSummary, setEstimateSummary] = useState(null);
    const [estimateAnalytics, setEstimateAnalytics] = useState(null);
    const [estSource, setEstSource] = useState('all');   // all | beta_estimate | customer_estimate
    const [estClean, setEstClean] = useState('all');     // all | clean | findings

    // reviewer metrics (Part 4)
    const [reviewerMetrics, setReviewerMetrics] = useState([]);

    // backlog / knowledge controls
    const [backlogType, setBacklogType] = useState('all');
    const [kbCategory, setKbCategory] = useState('all');
    const [kbSearch, setKbSearch] = useState('');

    // global search
    const [globalQuery, setGlobalQuery] = useState('');
    const [globalResults, setGlobalResults] = useState(null);

    // knowledge library (carrier corpus) + upload
    const [carrierLib, setCarrierLib] = useState(null);
    const [libTab, setLibTab] = useState('all');   // Knowledge Library family filter
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [allowUncertain, setAllowUncertain] = useState(false);

    // knowledge objects (Knowledge Extraction Engine)
    const [knowLib, setKnowLib] = useState(null);
    const [koType, setKoType] = useState('all');
    const [koImpact, setKoImpact] = useState('all');    // Business Impact filter
    const [koQuality, setKoQuality] = useState('all');  // quality tier filter
    const [koLowSignal, setKoLowSignal] = useState(false); // surface low-signal facts (off = signal over recall)
    const [koGroup, setKoGroup] = useState('byType');   // byType | byAccount | byCarrier
    const [koSearch, setKoSearch] = useState('');
    const [koSearchResults, setKoSearchResults] = useState(null);
    const [koBusy, setKoBusy] = useState(false);
    const [koMsg, setKoMsg] = useState(null);

    // detail drawers
    const [selectedJobId, setSelectedJobId] = useState(null);
    const [selectedObjectId, setSelectedObjectId] = useState(null);

    const headers = useMemo(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`,
    }), [session]);

    const getJSON = async (path) => {
        const res = await fetch(API + path, { headers });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
        return res.json();
    };

    const loadView = async (v = view) => {
        setLoading(true); setErr(null);
        try {
            if (v === 'dashboard') setDashboard(await getJSON('/api/admin/learning/dashboard'));
            else if (v === 'contributors') {
                setDashboard(await getJSON('/api/admin/learning/dashboard'));
                setReviewerMetrics((await getJSON('/api/admin/learning/reviewer-metrics')).reviewers || []);
            }
            else if (v === 'queue') setQueue((await getJSON('/api/admin/learning/queue')).jobs || []);
            else if (v === 'estimate_learning') {
                const r = await getJSON('/api/admin/learning/estimate-jobs');
                setEstimateJobs(r.jobs || []); setEstimateSummary(r.summary || null);
                setEstimateAnalytics(await getJSON('/api/admin/learning/estimate-analytics'));
            }
            else if (v === 'backlog') setBacklog((await getJSON('/api/admin/learning/backlog' + (backlogType !== 'all' ? `?type=${backlogType}` : ''))).items || []);
            else if (v === 'knowledge') setKnowledge((await getJSON('/api/admin/learning/knowledge' + (kbCategory !== 'all' ? `?category=${encodeURIComponent(kbCategory)}` : ''))).entries || []);
            else if (v === 'carrier') setCarrierLib(await getJSON('/api/admin/learning/carrier/library'));
            else if (v === 'knowledge_objects') {
                const qs = [];
                if (koType !== 'all') qs.push('object_type=' + encodeURIComponent(koType));
                if (koImpact !== 'all') qs.push('business_impact=' + encodeURIComponent(koImpact));
                if (koQuality !== 'all') qs.push('quality=' + encodeURIComponent(koQuality));
                if (koLowSignal) qs.push('includeLowSignal=true');
                setKnowLib(await getJSON('/api/admin/knowledge/library' + (qs.length ? '?' + qs.join('&') : '')));
            }
        } catch (e) { setErr(e.message); }
        setLoading(false);
    };

    // Knowledge Extraction Engine — transform the whole Knowledge Corpus into
    // structured Knowledge Objects (facts). Observe-only infrastructure.
    const runExtraction = async () => {
        setKoBusy(true); setKoMsg(null);
        try {
            const res = await fetch(API + '/api/admin/knowledge/backfill', { method: 'POST', headers, body: JSON.stringify({}) });
            const out = await res.json();
            if (!res.ok || !out.success) throw new Error(out.error || `HTTP ${res.status}`);
            setKoMsg(`✓ ${out.created} new fact(s) · ${out.linked} linked · ${out.versioned} versioned across ${out.documents} document(s).`);
            await loadView('knowledge_objects');
        } catch (e) { setKoMsg('⚠ ' + e.message); }
        setKoBusy(false);
    };

    const runKoSearch = async () => {
        if (!koSearch.trim()) { setKoSearchResults(null); return; }
        setLoading(true); setErr(null);
        try { setKoSearchResults(await getJSON('/api/admin/knowledge/search?q=' + encodeURIComponent(koSearch.trim()) + (koLowSignal ? '&includeLowSignal=true' : ''))); }
        catch (e) { setErr(e.message); }
        setLoading(false);
    };

    // Carrier document upload (multipart). Must NOT send a JSON Content-Type —
    // the browser sets the multipart boundary itself.
    const uploadCarrier = async (file) => {
        if (!file) return;
        setUploading(true); setUploadResult(null);
        try {
            const fd = new FormData();
            fd.append('document', file);
            // Admin override: allow uncertain/low-signal program docs through the
            // ingest guard (standards & manuals are still rejected server-side).
            if (allowUncertain) fd.append('allow_uncertain_program_doc', 'true');
            const res = await fetch(API + '/api/admin/learning/carrier/upload', {
                method: 'POST', headers: { Authorization: headers.Authorization }, body: fd,
            });
            const out = await res.json();
            if (!res.ok || !out.success) throw new Error(out.error || `HTTP ${res.status}`);
            setUploadResult(out);
            await loadView('carrier');
        } catch (e) { setUploadResult({ error: e.message }); }
        setUploading(false);
    };

    useEffect(() => { loadView(view); /* eslint-disable-line */ }, [view]);
    useEffect(() => { if (view === 'backlog') loadView('backlog'); /* eslint-disable-line */ }, [backlogType]);
    useEffect(() => { if (view === 'knowledge') loadView('knowledge'); /* eslint-disable-line */ }, [kbCategory]);
    useEffect(() => { if (view === 'knowledge_objects') loadView('knowledge_objects'); /* eslint-disable-line */ }, [koType, koImpact, koQuality, koLowSignal]);

    const runGlobalSearch = async () => {
        if (!globalQuery.trim()) return;
        setLoading(true); setErr(null);
        try { setGlobalResults(await getJSON('/api/admin/learning/search?q=' + encodeURIComponent(globalQuery.trim()))); }
        catch (e) { setErr(e.message); }
        setLoading(false);
    };

    // ── derived queue rows ────────────────────────────────────────────────────
    const queueRows = useMemo(() => {
        let rows = queue;
        const q = search.trim().toLowerCase();
        if (q) rows = rows.filter(r => [r.job_name, r.company_name, r.loss_type, r.document_type].filter(Boolean).some(v => v.toLowerCase().includes(q)));
        if (statusFilter !== 'all') rows = rows.filter(r => r.status === statusFilter);
        const dir = sortDir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const va = a[sortKey], vb = b[sortKey];
            if (typeof va === 'string' || typeof vb === 'string') return ((va || '') > (vb || '') ? 1 : -1) * dir;
            return ((va || 0) - (vb || 0)) * dir;
        });
    }, [queue, search, statusFilter, sortKey, sortDir]);

    const estimateRows = useMemo(() => {
        let rows = estimateJobs;
        if (estSource !== 'all') rows = rows.filter(r => (r.source_type || 'estimate') === estSource);
        if (estClean === 'clean') rows = rows.filter(r => r.clean_estimate === true);
        else if (estClean === 'findings') rows = rows.filter(r => r.clean_estimate === false);
        return rows;
    }, [estimateJobs, estSource, estClean]);

    const kbRows = useMemo(() => {
        const q = kbSearch.trim().toLowerCase();
        if (!q) return knowledge;
        return knowledge.filter(k => [k.title, k.body, k.category].filter(Boolean).some(v => v.toLowerCase().includes(q)));
    }, [knowledge, kbSearch]);

    const sortBy = (key) => () => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir('desc'); }
    };

    const SUBTABS = [
        ['dashboard', 'Dashboard'], ['queue', 'Learning Queue'], ['estimate_learning', 'Estimate Learning'], ['carrier', 'Knowledge Library'],
        ['knowledge_objects', 'Knowledge Objects'], ['consensus', 'Consensus'], ['recommendations', 'Recommendations'], ['backlog', 'Backlogs'], ['knowledge', 'Knowledge Base'],
        ['search', 'Global Search'], ['contributors', 'Contributors'],
    ];

    return (
        <div className="le-root">
            <div className="le-subnav">
                {SUBTABS.map(([k, label]) => (
                    <button key={k} className={'le-subtab ' + (view === k ? 'active' : '')} onClick={() => setView(k)}>{label}</button>
                ))}
                <button className="history-back-link le-refresh" onClick={() => loadView(view)}>↻ Refresh</button>
            </div>

            {err && <div className="le-error">⚠ {err}</div>}
            {loading && <div className="history-loading"><div className="spinner" /></div>}

            {/* ── DASHBOARD (Part 11) ─────────────────────────────────────────── */}
            {!loading && view === 'dashboard' && dashboard && (
                <div>
                    <div className="gd-section-title">Learning Pipeline</div>
                    <div className="gd-kpi-grid">
                        <KpiCard label="Queued Jobs" value={fmtNum(dashboard.cards.queued)} />
                        <KpiCard label="Analyzing" value={fmtNum(dashboard.cards.analyzing)} accent="var(--blue)" />
                        <KpiCard label="Ready For Review" value={fmtNum(dashboard.cards.readyForReview)} accent="var(--amber)" />
                        <KpiCard label="Approved Today" value={fmtNum(dashboard.cards.approvedToday)} accent="var(--green)" />
                        <KpiCard label="Rejected Today" value={fmtNum(dashboard.cards.rejectedToday)} />
                        <KpiCard label="Total Jobs" value={fmtNum(dashboard.cards.totalJobs)} />
                        <KpiCard label="Carrier Documents" value={fmtNum(dashboard.cards.carrierDocuments)} accent="var(--blue)" />
                        <KpiCard label="Carrier Jobs" value={fmtNum(dashboard.cards.carrierJobs)} accent="var(--blue)" />
                    </div>
                    <div className="gd-section-title">Observation Mix</div>
                    <div className="gd-kpi-grid">
                        <KpiCard label="Parser Candidates" value={fmtNum(dashboard.cards.parserCandidates)} />
                        <KpiCard label="Rule Candidates" value={fmtNum(dashboard.cards.ruleCandidates)} />
                        <KpiCard label="Carrier Observations" value={fmtNum(dashboard.cards.carrierObservations)} />
                        <KpiCard label="Knowledge Observations" value={fmtNum(dashboard.cards.knowledgeObservations)} />
                        <KpiCard label="Regression Suggestions" value={fmtNum(dashboard.cards.regressionSuggestions)} />
                        <KpiCard label="Avg Confidence" value={fmtPct(dashboard.cards.avgConfidence)} />
                        <KpiCard label="Highest Confidence" value={fmtPct(dashboard.cards.highestConfidence)} accent="var(--green)" />
                        <KpiCard label="Pending Observations" value={fmtNum(dashboard.cards.pendingTotal)} accent="var(--amber)" />
                    </div>
                    <div className="gd-section-title">Automatic Learning Pipeline</div>
                    <div className="le-pipeline">
                        {['Estimate Uploaded', 'Audit Completed', 'Corpus Queue', 'Learning Job', 'AI Analysis', 'Observations', 'Human Review', 'Engineering Backlog', 'Approved Work'].map((s, i, a) => (
                            <React.Fragment key={s}>
                                <span className="le-pipe-step">{s}</span>
                                {i < a.length - 1 && <span className="le-pipe-arrow">→</span>}
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="le-note">Nothing ever bypasses human approval. The Learning Engine observes and recommends — it never modifies production.</div>
                </div>
            )}

            {/* ── LEARNING QUEUE (Part 4) ─────────────────────────────────────── */}
            {!loading && view === 'queue' && (
                <div>
                    <div className="le-controls">
                        <input className="admin-plan-select le-search" placeholder="Search company / estimate / loss type…" value={search} onChange={e => setSearch(e.target.value)} />
                        <select className="admin-plan-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                            <option value="all">All statuses</option>
                            {['Queued', 'Analyzing', 'Ready For Review', 'Approved', 'Rejected', 'Needs Investigation'].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <span className="le-count">{queueRows.length} job(s)</span>
                    </div>
                    <div className="admin-table-wrap">
                        <table className="admin-table gd-table">
                            <thead>
                                <tr>
                                    <th className="gd-sortable" onClick={sortBy('company_name')}>Company</th>
                                    <th className="gd-sortable" onClick={sortBy('job_name')}>Estimate</th>
                                    <th className="gd-sortable" onClick={sortBy('loss_type')}>Loss Type</th>
                                    <th className="gd-sortable gd-num" onClick={sortBy('avg_confidence')}>Confidence</th>
                                    <th className="gd-sortable gd-num" onClick={sortBy('observation_count')}>Observations</th>
                                    <th className="gd-sortable" onClick={sortBy('priority')}>Priority</th>
                                    <th className="gd-sortable" onClick={sortBy('status')}>Status</th>
                                    <th className="gd-sortable" onClick={sortBy('created_at')}>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {queueRows.length === 0 && <tr><td colSpan={8} style={{ color: 'var(--text-muted)' }}>No learning jobs yet. Opted-in estimates create jobs automatically.</td></tr>}
                                {queueRows.map(j => (
                                    <tr key={j.id} className="le-clickable" onClick={() => setSelectedJobId(j.id)}>
                                        <td>{j.company_name || '—'}</td>
                                        <td>{j.job_name || '—'}{j.learning_version > 1 && <span className="le-vtag">v{j.learning_version}</span>}</td>
                                        <td>{j.loss_type || '—'}</td>
                                        <td className="gd-num">{fmtPct(j.avg_confidence)}</td>
                                        <td className="gd-num">{fmtNum(j.observation_count)}</td>
                                        <td>{j.priority ? <Badge text={j.priority} color={PRI_COLOR[j.priority]} /> : '—'}</td>
                                        <td><Badge text={j.status} color={STATUS_COLOR[j.status]} /></td>
                                        <td>{fmtDate(j.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── ESTIMATE LEARNING (Part 3) ──────────────────────────────────── */}
            {!loading && view === 'estimate_learning' && (
                <div>
                    {estimateSummary && (
                        <>
                            <div className="gd-section-title">Beta Estimate Learning</div>
                            <div className="gd-kpi-grid">
                                <KpiCard label="Estimate Jobs" value={fmtNum(estimateSummary.total)} />
                                <KpiCard label="Beta Estimates" value={fmtNum(estimateSummary.beta)} accent="var(--blue)" />
                                <KpiCard label="Customer Estimates" value={fmtNum(estimateSummary.customer)} accent="var(--green)" />
                                <KpiCard label="Clean Estimates" value={fmtNum(estimateSummary.clean)} accent="var(--green)" />
                                <KpiCard label="With Findings" value={fmtNum(estimateSummary.withFindings)} accent="var(--amber)" />
                                <KpiCard label="Opportunity $" value={fmt$(estimateSummary.totalOpportunity)} />
                                <KpiCard label="Observations" value={fmtNum(estimateSummary.totalObservations)} />
                            </div>
                        </>
                    )}
                    {/* Part 8: estimate-learning analytics */}
                    {estimateAnalytics && (
                        <>
                            <div className="gd-section-title">Estimate Learning Analytics</div>
                            <div className="gd-kpi-grid">
                                <KpiCard label="Total Beta Estimates" value={fmtNum(estimateAnalytics.totalBetaEstimates)} accent="var(--blue)" />
                                <KpiCard label="Learning Jobs" value={fmtNum(estimateAnalytics.learningJobs)} />
                                <KpiCard label="Observations" value={fmtNum(estimateAnalytics.observations)} />
                                <KpiCard label="Approval Rate" value={fmtPct(estimateAnalytics.approvalRate)} accent="var(--green)" />
                                <KpiCard label="Knowledge Objects" value={estimateAnalytics.knowledgeObjectsCreated == null ? '—' : fmtNum(estimateAnalytics.knowledgeObjectsCreated)} />
                                <KpiCard label="Recommendations" value={estimateAnalytics.recommendationsStrengthened == null ? '—' : fmtNum(estimateAnalytics.recommendationsStrengthened)} />
                            </div>
                            <div className="le-top-grid">
                                <TopList title="Top Recurring Issues" rows={estimateAnalytics.topRecurringIssues} />
                                <TopList title="Top Recurring Carriers" rows={estimateAnalytics.topRecurringCarriers} />
                                <TopList title="Top Recurring Loss Types" rows={estimateAnalytics.topRecurringLossTypes} />
                            </div>
                        </>
                    )}
                    <div className="le-controls">
                        <select className="admin-plan-select" value={estSource} onChange={e => setEstSource(e.target.value)}>
                            <option value="all">All sources</option>
                            <option value="beta_estimate">Beta estimate</option>
                            <option value="customer_estimate">Customer estimate</option>
                            <option value="estimate">Legacy</option>
                        </select>
                        <select className="admin-plan-select" value={estClean} onChange={e => setEstClean(e.target.value)}>
                            <option value="all">Clean &amp; findings</option>
                            <option value="clean">Clean only</option>
                            <option value="findings">Findings only</option>
                        </select>
                        <span className="le-count">{estimateRows.length} estimate job(s)</span>
                    </div>
                    <div className="admin-table-wrap">
                        <table className="admin-table gd-table">
                            <thead>
                                <tr>
                                    <th>Company</th><th>Estimate</th><th>File</th><th>Type</th>
                                    <th>Carrier</th><th>Loss</th><th className="gd-num">Total</th>
                                    <th className="gd-num">Opportunity</th><th>Clean?</th>
                                    <th className="gd-num">Obs</th><th>Source</th><th>Status</th><th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {estimateRows.length === 0 && <tr><td colSpan={13} style={{ color: 'var(--text-muted)' }}>No estimate learning jobs yet. Opted-in beta/customer estimates create jobs automatically.</td></tr>}
                                {estimateRows.map(j => (
                                    <tr key={j.id} className="le-clickable" onClick={() => setSelectedJobId(j.id)}>
                                        <td>{j.company_name || '—'}</td>
                                        <td>{j.job_name || '—'}{j.learning_version > 1 && <span className="le-vtag">v{j.learning_version}</span>}</td>
                                        <td>{j.file_name || '—'}</td>
                                        <td>{j.estimate_type || '—'}</td>
                                        <td>{j.carrier || '—'}</td>
                                        <td>{j.loss_type || '—'}</td>
                                        <td className="gd-num">{j.grand_total != null ? fmt$(j.grand_total) : '—'}</td>
                                        <td className="gd-num">{j.opportunity_total != null ? fmt$(j.opportunity_total) : '—'}</td>
                                        <td>{j.clean_estimate == null ? '—' : <Badge text={j.clean_estimate ? 'Clean' : 'Findings'} color={j.clean_estimate ? 'var(--green)' : 'var(--amber)'} />}</td>
                                        <td className="gd-num">{fmtNum(j.observation_count)}</td>
                                        <td>{(j.source_type || '').replace('_estimate', '') || '—'}</td>
                                        <td><Badge text={j.status} color={STATUS_COLOR[j.status]} /></td>
                                        <td>{fmtDate(j.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="le-note">Read-only. Open a job to review its observations — nothing here is auto-approved or fed into production.</div>
                </div>
            )}

            {/* ── ENGINEERING BACKLOGS (Part 7) ───────────────────────────────── */}
            {!loading && view === 'backlog' && (
                <div>
                    <div className="le-controls">
                        <button className={'le-pill ' + (backlogType === 'all' ? 'active' : '')} onClick={() => setBacklogType('all')}>All</button>
                        {BACKLOG_TYPES.map(t => (
                            <button key={t.key} className={'le-pill ' + (backlogType === t.key ? 'active' : '')} onClick={() => setBacklogType(t.key)}>{t.label}</button>
                        ))}
                        <span className="le-count">{backlog.length} item(s)</span>
                    </div>
                    <div className="admin-table-wrap">
                        <table className="admin-table gd-table">
                            <thead><tr><th>Type</th><th>Title</th><th>Priority</th><th>Status</th><th>Source Estimate</th><th>Created</th></tr></thead>
                            <tbody>
                                {backlog.length === 0 && <tr><td colSpan={6} style={{ color: 'var(--text-muted)' }}>No backlog items. They appear when you approve observations.</td></tr>}
                                {backlog.map(b => (
                                    <tr key={b.id}>
                                        <td>{BACKLOG_TYPES.find(t => t.key === b.backlog_type)?.label || b.backlog_type}</td>
                                        <td title={b.notes}>{b.title}</td>
                                        <td><Badge text={b.priority} color={PRI_COLOR[b.priority]} /></td>
                                        <td>
                                            <select className="admin-plan-select le-mini" value={b.status}
                                                onChange={async (e) => {
                                                    const status = e.target.value;
                                                    await fetch(API + '/api/admin/learning/backlog/' + b.id, { method: 'PATCH', headers, body: JSON.stringify({ status }) });
                                                    setBacklog(prev => prev.map(x => x.id === b.id ? { ...x, status } : x));
                                                }}>
                                                {['Open', 'In Progress', 'Done', 'Wont Do'].map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </td>
                                        <td>{b.source_estimate || '—'}</td>
                                        <td>{fmtDate(b.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── KNOWLEDGE BASE (Part 9) ─────────────────────────────────────── */}
            {!loading && view === 'knowledge' && (
                <div>
                    <div className="le-controls">
                        <input className="admin-plan-select le-search" placeholder="Search knowledge…" value={kbSearch} onChange={e => setKbSearch(e.target.value)} />
                        <select className="admin-plan-select" value={kbCategory} onChange={e => setKbCategory(e.target.value)}>
                            <option value="all">All categories</option>
                            {KB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <span className="le-count">{kbRows.length} entr(ies)</span>
                    </div>
                    {kbRows.length === 0 && <div className="le-empty">No knowledge entries yet. Approved observations land here.</div>}
                    <div className="le-kb-grid">
                        {kbRows.map(k => (
                            <div className="le-kb-card" key={k.id}>
                                <div className="le-kb-head">
                                    <Badge text={k.category} color="var(--blue)" />
                                    {k.confidence != null && <Badge text={fmtPct(k.confidence)} color={CONF_COLOR[k.confidence < 0.5 ? 'low' : k.confidence < 0.75 ? 'medium' : 'high']} />}
                                </div>
                                <div className="le-kb-title">{k.title}</div>
                                <div className="le-kb-body">{k.body}</div>
                                <div className="le-kb-foot">Estimate: {k.source_estimate || '—'} · audit #{k.audit_id}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── GLOBAL SEARCH (Part 10) ─────────────────────────────────────── */}
            {!loading && view === 'search' && (
                <div>
                    <div className="le-controls">
                        <input className="admin-plan-select le-search" placeholder="Search customers, estimates, observations, knowledge, backlog…"
                            value={globalQuery} onChange={e => setGlobalQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && runGlobalSearch()} />
                        <button className="history-back-link" onClick={runGlobalSearch}>Search</button>
                    </div>
                    {globalResults && (
                        <div>
                            <SearchGroup title="Estimates / Jobs" rows={globalResults.jobs} render={j => (
                                <span className="le-clickable" onClick={() => setSelectedJobId(j.id)}>{j.job_name || '—'} · {j.loss_type || j.document_type} <Badge text={j.status} color={STATUS_COLOR[j.status]} /></span>
                            )} />
                            <SearchGroup title="Observations" rows={globalResults.observations} render={o => (
                                <span className="le-clickable" onClick={() => setSelectedJobId(o.job_id)}><b>{o.observation_type}</b> — {o.reasoning?.slice(0, 140)}</span>
                            )} />
                            <SearchGroup title="Carrier Documents" rows={globalResults.carrierDocuments} render={c => <span><b>{c.carrier}</b> · {c.document_type}{c.version ? ` v${c.version}` : ` (seq ${c.version_seq})`}{c.region ? ` · ${c.region}` : ''}</span>} />
                            <SearchGroup title="Knowledge" rows={globalResults.knowledge} render={k => <span><b>[{k.category}]</b> {k.title}</span>} />
                            <SearchGroup title="Backlog" rows={globalResults.backlog} render={b => <span><b>{b.backlog_type}</b> · {b.title} <Badge text={b.status} color={STATUS_COLOR[b.status]} /></span>} />
                            <SearchGroup title="Customers" rows={globalResults.customers} render={c => <span>{c.company_name || c.contact_name || c.email}</span>} />
                        </div>
                    )}
                </div>
            )}

            {/* ── CONTRIBUTORS (Part 12) ──────────────────────────────────────── */}
            {!loading && view === 'contributors' && dashboard && (
                <div>
                    <div className="le-note">Which beta users are improving Audee the most. Knowledge Score = jobs ×1 + approved obs ×2 + knowledge ×3 + reported FPs ×2.</div>
                    <div className="admin-table-wrap">
                        <table className="admin-table gd-table">
                            <thead><tr><th>Company</th><th>Contact</th><th className="gd-num">Jobs</th><th className="gd-num">Approved Obs</th><th className="gd-num">Pending</th><th className="gd-num">Knowledge</th><th className="gd-num">FPs Reported</th><th className="gd-num">Feature Reqs</th><th className="gd-num">Knowledge Score</th></tr></thead>
                            <tbody>
                                {(dashboard.contributors || []).length === 0 && <tr><td colSpan={9} style={{ color: 'var(--text-muted)' }}>No attributed contributions yet.</td></tr>}
                                {(dashboard.contributors || []).map(c => (
                                    <tr key={c.customer_id}>
                                        <td>{c.company_name || '—'}</td>
                                        <td>{c.contact_name || '—'}</td>
                                        <td className="gd-num">{fmtNum(c.learningJobs)}</td>
                                        <td className="gd-num" style={{ color: 'var(--green)' }}>{fmtNum(c.approvedObservations)}</td>
                                        <td className="gd-num">{fmtNum(c.pendingObservations)}</td>
                                        <td className="gd-num">{fmtNum(c.knowledgeContributions)}</td>
                                        <td className="gd-num">{fmtNum(c.falsePositivesReported)}</td>
                                        <td className="gd-num">{fmtNum(c.featureRequests)}</td>
                                        <td className="gd-num" style={{ fontWeight: 700, color: 'var(--amber)' }}>{fmtNum(c.knowledgeScore)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Part 4: per-reviewer learning metrics */}
                    <div className="gd-section-title" style={{ marginTop: 18 }}>Reviewer Metrics</div>
                    <div className="le-note">How the internal team is triaging observations. Review time is created → reviewed.</div>
                    <div className="admin-table-wrap">
                        <table className="admin-table gd-table">
                            <thead><tr><th>Reviewer</th><th className="gd-num">Reviewed</th><th className="gd-num">Approved</th><th className="gd-num">Rejected</th><th className="gd-num">Approval %</th><th className="gd-num">Duplicate %</th><th className="gd-num">False Alarm %</th><th className="gd-num">Avg Review Time</th></tr></thead>
                            <tbody>
                                {reviewerMetrics.length === 0 && <tr><td colSpan={8} style={{ color: 'var(--text-muted)' }}>No reviewed observations yet.</td></tr>}
                                {reviewerMetrics.map(r => (
                                    <tr key={r.reviewer}>
                                        <td>{r.reviewer}</td>
                                        <td className="gd-num">{fmtNum(r.reviewed)}</td>
                                        <td className="gd-num" style={{ color: 'var(--green)' }}>{fmtNum(r.approved)}</td>
                                        <td className="gd-num">{fmtNum(r.rejected)}</td>
                                        <td className="gd-num">{fmtPct(r.approvalPct)}</td>
                                        <td className="gd-num">{fmtPct(r.duplicatePct)}</td>
                                        <td className="gd-num">{fmtPct(r.falseAlarmPct)}</td>
                                        <td className="gd-num">{r.avgReviewMs != null ? Math.round(r.avgReviewMs / 1000) + 's' : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── KNOWLEDGE OBJECTS (Knowledge Extraction Engine) ─────────────── */}
            {!loading && view === 'knowledge_objects' && (
                <div>
                    <div className="le-note">
                        Knowledge Extraction Engine — every document in the Knowledge Corpus is transformed into structured, searchable <b>Knowledge Objects</b> (facts: payment terms, photo requirements, required forms, service lines, drying guidance, …). Each fact cites its source text and traces back to the originating document. Identical facts across revisions are linked (never duplicated); changed facts are versioned with full history. This is infrastructure — it creates no production rules and no Carrier Intelligence.
                    </div>
                    <div className="le-upload">
                        <button className={'le-btn le-approve' + (koBusy ? ' le-disabled' : '')} disabled={koBusy} onClick={runExtraction}>
                            {koBusy ? 'Extracting…' : '⚙ Extract Knowledge Corpus → Objects'}
                        </button>
                        {koMsg && <span className={koMsg.startsWith('⚠') ? 'le-error' : 'le-upload-ok'} style={{ margin: 0 }}>{koMsg}</span>}
                    </div>

                    {/* Part 8 — Knowledge Search */}
                    <div className="le-controls">
                        <input className="admin-plan-select le-search" placeholder="Search facts — e.g. moisture readings, Xactimate, photos, payment…"
                            value={koSearch} onChange={e => setKoSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && runKoSearch()} />
                        <button className="history-back-link" onClick={runKoSearch}>Search</button>
                        {koSearchResults && <button className="history-back-link" onClick={() => { setKoSearch(''); setKoSearchResults(null); }}>Clear</button>}
                    </div>

                    {koSearchResults ? (
                        <div>
                            <div className="gd-section-title">Search results for “{koSearchResults.query}” ({(koSearchResults.objects || []).length})</div>
                            {(koSearchResults.objects || []).length === 0 && <div className="le-empty">No matching facts.</div>}
                            <div className="le-kb-grid">
                                {(koSearchResults.objects || []).map(o => <KnowledgeObjectCard key={o.id} o={o} onOpen={() => setSelectedObjectId(o.id)} />)}
                            </div>
                        </div>
                    ) : knowLib && (
                        <div>
                            {/* Part 4 — facet filters */}
                            <div className="le-controls">
                                <select className="admin-plan-select" value={koType} onChange={e => setKoType(e.target.value)}>
                                    <option value="all">All object types</option>
                                    {(knowLib.typeCatalog || []).map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                                <select className="admin-plan-select" value={koImpact} onChange={e => setKoImpact(e.target.value)}>
                                    <option value="all">All business impact</option>
                                    {(knowLib.businessImpactCatalog || []).map(b => <option key={b} value={b}>{b}</option>)}
                                </select>
                                <select className="admin-plan-select" value={koQuality} onChange={e => setKoQuality(e.target.value)}>
                                    <option value="all">All quality</option>
                                    {(knowLib.qualityTierCatalog || ['high', 'medium', 'low']).map(t => <option key={t} value={t}>{t} signal</option>)}
                                </select>
                                <label className="le-pill" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    <input type="checkbox" checked={koLowSignal} onChange={e => setKoLowSignal(e.target.checked)} />
                                    Show low-signal
                                </label>
                                <div style={{ display: 'inline-flex', gap: 4 }}>
                                    {[['byType', 'By Type'], ['byAccount', 'By Account'], ['byCarrier', 'By Carrier']].map(([k, label]) => (
                                        <button key={k} className={'le-pill ' + (koGroup === k ? 'active' : '')} onClick={() => setKoGroup(k)}>{label}</button>
                                    ))}
                                </div>
                                <span className="le-count">{knowLib.totalObjects} high-signal fact(s)</span>
                                {!koLowSignal && knowLib.suppressedCount > 0 && (
                                    <button className="history-back-link" onClick={() => setKoLowSignal(true)} title="Quality Engine hid these low-signal facts (truncated fragments, boilerplate, generic catch-all below threshold)">
                                        +{knowLib.suppressedCount} low-signal hidden — show all
                                    </button>
                                )}
                            </div>

                            {knowLib.totalObjects === 0 && <div className="le-empty">No Knowledge Objects yet. Click “Extract Knowledge Corpus → Objects” to populate from the corpus.</div>}

                            {(knowLib[koGroup] || []).map(group => (
                                <div key={group.key} className="le-carrier-group">
                                    <div className="le-carrier-name">
                                        {group.key} <span className="le-count">{group.count} fact(s)</span>
                                        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, marginLeft: 8 }}>
                                            {Object.entries(group.byType || {}).map(([t, n]) => <Badge key={t} text={`${t} ${n}`} color="var(--blue)" />)}
                                        </span>
                                    </div>
                                    <div className="le-kb-grid">
                                        {group.objects.map(o => <KnowledgeObjectCard key={o.id} o={o} onOpen={() => setSelectedObjectId(o.id)} />)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── CARRIER LIBRARY (Part 7) ───────────────────────────────────── */}
            {!loading && view === 'carrier' && (
                <div>
                    <div className="le-note">
                        Knowledge Corpus — upload carrier documents, SERVPRO program bulletins, and (later) manufacturer/vendor docs. Each is auto-classified (issuer · account/client · carrier-if-insurer · document type · version), stored as a new version (never overwritten), and turned into a Learning Job with evidence-cited observations. This is infrastructure — no production or carrier-intelligence rules are created.
                    </div>
                    <div className="le-upload">
                        <label className={'le-btn le-approve' + (uploading ? ' le-disabled' : '')}>
                            {uploading ? 'Uploading & analyzing…' : '⬆ Upload Document (PDF)'}
                            <input type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} disabled={uploading}
                                onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; uploadCarrier(f); }} />
                        </label>
                        <label className="le-note" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0, cursor: 'pointer' }} title="Standards and manuals are always rejected. This only lets an uncertain/low-signal carrier program document through.">
                            <input type="checkbox" checked={allowUncertain} disabled={uploading}
                                onChange={e => setAllowUncertain(e.target.checked)} />
                            Allow uncertain program doc (override)
                        </label>
                        {uploadResult?.error && <span className="le-error" style={{ margin: 0 }}>⚠ {uploadResult.error}</span>}
                        {uploadResult?.success && (
                            <span className="le-upload-ok">
                                ✓ {uploadResult.classification.issuer === 'SERVPRO'
                                    ? `SERVPRO · ${uploadResult.classification.account || uploadResult.classification.document_type}`
                                    : `${uploadResult.classification.carrier} · ${uploadResult.classification.document_type}`}
                                {uploadResult.classification.version ? ` v${uploadResult.classification.version}` : ''} —
                                {' '}{uploadResult.observationCount} observation(s).{' '}
                                <span className="le-clickable" onClick={() => setSelectedJobId(uploadResult.job.id)}>Open job →</span>
                            </span>
                        )}
                    </div>

                    {carrierLib && (
                        <div>
                            <div className="gd-section-title">Knowledge Library ({carrierLib.totalDocuments} document{carrierLib.totalDocuments === 1 ? '' : 's'})</div>
                            {(carrierLib.libraryTabs || []).length > 0 && (
                                <div className="le-subnav" style={{ marginBottom: 10 }}>
                                    {carrierLib.libraryTabs.map(t => (
                                        <button key={t.key} className={'le-subtab ' + (libTab === t.key ? 'active' : '')}
                                            onClick={() => setLibTab(t.key)} disabled={t.key !== 'all' && !t.count}>
                                            {t.label} <span className="le-count">{t.count}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {carrierLib.carriers.length === 0 && <div className="le-empty">No documents yet. Upload one above.</div>}
                            {carrierLib.carriers.map(c => {
                                const docs = c.documents.filter(d => libTab === 'all' || d.library_tab === libTab);
                                if (!docs.length) return null;
                                return (
                                    <div key={c.carrier} className="le-carrier-group">
                                        <div className="le-carrier-name">{c.carrier} <span className="le-count">{docs.length} doc(s)</span></div>
                                        <div className="admin-table-wrap">
                                            <table className="admin-table gd-table">
                                                <thead><tr><th>Family</th><th>Account / Client</th><th>Document Type</th><th>Bulletin / Version</th><th>Status</th><th>Date</th><th className="gd-num">Jobs</th><th className="gd-num">Obs</th><th>Uploaded</th></tr></thead>
                                                <tbody>
                                                    {docs.map(d => (
                                                        <tr key={d.id} className={d.job_id ? 'le-clickable' : ''} onClick={() => d.job_id && setSelectedJobId(d.job_id)}>
                                                            <td>{d.corpus_family_label || '—'}</td>
                                                            <td>{d.account_client || (d.account_is_insurer === false && d.issuer === 'SERVPRO' ? '(commercial)' : '—')}</td>
                                                            <td>{d.document_type}</td>
                                                            <td>{d.bulletin_code ? d.bulletin_code : (d.version ? `v${d.version}` : '—')} <span className="le-vtag">seq {d.version_seq}</span></td>
                                                            <td>{d.program_status || '—'}</td>
                                                            <td>{d.revision_date ? fmtDate(d.revision_date) : '—'}</td>
                                                            <td className="gd-num">{fmtNum(d.learningJobs)}</td>
                                                            <td className="gd-num">{fmtNum(d.observations)}</td>
                                                            <td>{fmtDate(d.created_at)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ── KNOWLEDGE CONSENSUS (Consensus Engine — sits on top of the Objects) */}
            {view === 'consensus' && <KnowledgeConsensus session={session} />}

            {/* ── RECOMMENDATION ENGINE (internal, explainable; not customer-facing) */}
            {view === 'recommendations' && <RecommendationEngine session={session} />}

            {selectedJobId && (
                <JobDetail jobId={selectedJobId} headers={headers} onClose={() => setSelectedJobId(null)} onChanged={() => loadView(view)} setSelectedJobId={setSelectedJobId} />
            )}
            {selectedObjectId && (
                <KnowledgeObjectDetail objectId={selectedObjectId} headers={headers} onClose={() => setSelectedObjectId(null)} setSelectedObjectId={setSelectedObjectId} />
            )}
        </div>
    );
}

// ── Knowledge Object card (Knowledge Extraction Engine) ─────────────────────
function KnowledgeObjectCard({ o, onOpen }) {
    return (
        <div className="le-kb-card le-clickable" onClick={onOpen}>
            <div className="le-kb-head">
                <Badge text={o.object_type} color="var(--blue)" />
                {o.quality_tier && <Badge text={`${o.quality_tier} · ${Math.round((o.quality_score || 0) * 100)}`} color={CONF_COLOR[o.quality_tier] || 'var(--blue)'} />}
                {o.is_high_signal === false && <Badge text="low-signal" color="var(--text-dim, #888)" />}
                {o.object_version > 1 && <span className="le-vtag">v{o.object_version}</span>}
            </div>
            <div className="le-kb-title">{o.title}</div>
            <div className="le-kb-body">{o.description}</div>
            {Array.isArray(o.business_impact) && o.business_impact.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                    {o.business_impact.map(b => <Badge key={b} text={b} color="var(--amber)" />)}
                </div>
            )}
            <div className="le-kb-foot">
                {[o.issuer, o.account, o.carrier, o.bulletin_code].filter(Boolean).join(' · ') || o.document_family || '—'}
                {o.effective_date ? ` · eff ${fmtDate(o.effective_date)}` : ''}
                {Array.isArray(o.source_document_ids) && o.source_document_ids.length > 1 ? ` · ${o.source_document_ids.length} sources` : ''}
            </div>
        </div>
    );
}

// ── Knowledge Object detail drawer: version history (Part 7) + source docs (Part 5)
function KnowledgeObjectDetail({ objectId, headers, onClose, setSelectedObjectId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let live = true;
        (async () => {
            setLoading(true);
            try {
                const res = await fetch(API + '/api/admin/knowledge/object/' + objectId, { headers });
                const out = await res.json();
                if (live) setData(out);
            } catch (e) { if (live) setData({ error: e.message }); }
            if (live) setLoading(false);
        })();
        return () => { live = false; };
    }, [objectId, headers]);

    const o = data?.object;
    return (
        <div className="le-drawer-overlay" onClick={onClose}>
            <div className="le-drawer" onClick={e => e.stopPropagation()}>
                <div className="le-drawer-head"><button className="history-back-link" onClick={onClose}>✕ Close</button></div>
                {loading && <div className="history-loading"><div className="spinner" /></div>}
                {data?.error && <div className="le-error">⚠ {data.error}</div>}
                {!loading && o && (
                    <div className="le-drawer-body">
                        <div className="le-drawer-title">{o.title} <span className="le-vtag">v{o.object_version}{o.is_current ? '' : ' (superseded)'}</span></div>
                        <div className="le-meta-grid">
                            <div><span>Object Type</span><b>{o.object_type}</b></div>
                            <div><span>Confidence</span><b>{o.confidence_label} · {fmtPct(o.confidence)}</b></div>
                            <div><span>Document Family</span><b>{o.document_family || '—'}</b></div>
                            <div><span>Issuer</span><b>{o.issuer || '—'}</b></div>
                            <div><span>Account / Client</span><b>{o.account || '—'}</b></div>
                            <div><span>Carrier</span><b>{o.carrier || '—'}</b></div>
                            <div><span>Bulletin Code</span><b>{o.bulletin_code || '—'}</b></div>
                            <div><span>Lineage Key</span><b>{o.lineage_key || o.lineage_scope || '—'}</b></div>
                            <div><span>Effective Date</span><b>{o.effective_date ? fmtDate(o.effective_date) : '—'}</b></div>
                            <div><span>Program Status</span><b>{o.program_status || '—'}</b></div>
                            <div><span>Service Lines</span><b>{Array.isArray(o.service_lines) && o.service_lines.length ? o.service_lines.join(', ') : '—'}</b></div>
                            <div><span>Business Impact</span><b>{Array.isArray(o.business_impact) && o.business_impact.length ? o.business_impact.join(', ') : '—'}</b></div>
                            <div><span>Quality Signal</span><b>{o.quality_tier || '—'} · {Math.round((o.quality_score || 0) * 100)}/100 · {o.is_high_signal ? 'surfaced' : 'hidden by default'}</b></div>
                            <div><span>Extractor</span><b>{o.extractor || '—'} {o.extractor_version || ''}</b></div>
                        </div>
                        <div className="le-obs-card">
                            <div className="le-obs-reason">{o.description}</div>
                            {o.supporting_evidence && <Evidence label="Supporting Evidence (cited source text)" data={o.supporting_evidence} />}
                        </div>

                        {/* Part 13 — Knowledge Quality Engine: why this fact scored as it did */}
                        {data.quality && (
                            <div className="le-note" style={{ marginTop: 8 }}>
                                <b>Quality Engine:</b> {data.quality.tier} signal ({Math.round((data.quality.score || 0) * 100)}/100) — {data.quality.is_high_signal ? 'surfaced by default.' : 'hidden by default (below threshold / flagged as noise).'}
                                {data.quality.signals && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                                        {Object.entries(data.quality.signals).map(([k, v]) => (
                                            <Badge key={k} text={typeof v === 'boolean' ? k : `${k}:${v}`}
                                                color={['truncated', 'boilerplate', 'tooShort', 'tooLong'].includes(k) ? 'var(--red, #c0392b)' : 'var(--blue)'} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Part 5 — source documents (no orphaned data) */}
                        <div className="gd-section-title">Source Document{(data.sourceDocuments || []).length === 1 ? '' : 's'} ({(data.sourceDocuments || []).length})</div>
                        {(data.sourceDocuments || []).map(d => (
                            <div key={d.id} className="le-trace">
                                {[d.issuer, d.carrier, d.account_client, d.document_type].filter(Boolean).join(' · ')}
                                {d.bulletin_code ? ` · ${d.bulletin_code}` : (d.version ? ` · v${d.version}` : '')}
                                {' · '}{d.original_filename || 'document.pdf'}
                                {d.content_sha256 && <> · sha {d.content_sha256.slice(0, 10)}…</>}
                            </div>
                        ))}

                        {/* Part 7 — version history */}
                        {(data.versions || []).length > 1 && (
                            <>
                                <div className="gd-section-title">Version History ({data.versions.length})</div>
                                <div className="le-versions">
                                    {data.versions.map(v => (
                                        <button key={v.id} className={'le-version-chip ' + (v.id === o.id ? 'active' : '')} onClick={() => setSelectedObjectId(v.id)}>
                                            v{v.object_version}{v.is_current ? ' · current' : ''} · {v.effective_date ? fmtDate(v.effective_date) : fmtDate(v.created_at)}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function SearchGroup({ title, rows, render }) {
    if (!rows || !rows.length) return null;
    return (
        <div className="le-search-group">
            <div className="gd-section-title">{title} ({rows.length})</div>
            <ul className="le-search-list">{rows.map((r, i) => <li key={r.id || i}>{render(r)}</li>)}</ul>
        </div>
    );
}

// ── Job Detail drawer (Parts 5 / 6 / 16 / 17) ───────────────────────────────
function JobDetail({ jobId, headers, onClose, onChanged, setSelectedJobId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    // Part 3 bulk selection · Part 5 preview · Part 6 duplicates (keyed by obs id)
    const [selected, setSelected] = useState(() => new Set());
    const [previews, setPreviews] = useState({});
    const [dups, setDups] = useState({});
    // Knowledge Triage — filter observations by AI triage label.
    const [triageFilter, setTriageFilter] = useState('all');

    const load = async () => {
        setLoading(true);
        try {
            const res = await fetch(API + '/api/admin/learning/job/' + jobId, { headers });
            setData(await res.json());
        } catch (e) { setData({ error: e.message }); }
        setLoading(false);
    };
    useEffect(() => { load(); setSelected(new Set()); setPreviews({}); setDups({}); /* eslint-disable-line */ }, [jobId]);

    const review = async (obsId, action) => {
        setBusy(obsId + action);
        try {
            await fetch(API + `/api/admin/learning/observation/${obsId}/review`, { method: 'POST', headers, body: JSON.stringify({ action }) });
            await load(); onChanged && onChanged();
        } catch (e) { /* surfaced on reload */ }
        setBusy(null);
    };

    // Part 3: bulk review of every selected observation.
    const bulkReview = async (action) => {
        const ids = [...selected];
        if (!ids.length) return;
        setBusy('bulk');
        try {
            await fetch(API + '/api/admin/learning/observations/bulk-review', { method: 'POST', headers, body: JSON.stringify({ ids, action }) });
            setSelected(new Set()); await load(); onChanged && onChanged();
        } catch (e) { /* */ }
        setBusy(null);
    };
    const toggleSel = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

    // Part 5: promotion preview (read-only).
    const loadPreview = async (obsId) => {
        setBusy('preview' + obsId);
        try {
            const res = await fetch(API + `/api/admin/learning/observation/${obsId}/promotion-preview`, { headers });
            const j = await res.json();
            setPreviews(p => ({ ...p, [obsId]: j }));
        } catch (e) { /* */ }
        setBusy(null);
    };

    // Part 6: duplicate detection + relate action.
    const loadDups = async (obsId) => {
        setBusy('dups' + obsId);
        try {
            const res = await fetch(API + `/api/admin/learning/observation/${obsId}/duplicates`, { headers });
            const j = await res.json();
            setDups(d => ({ ...d, [obsId]: j }));
        } catch (e) { /* */ }
        setBusy(null);
    };
    const relate = async (obsId, action, target) => {
        setBusy('relate' + obsId);
        try {
            await fetch(API + `/api/admin/learning/observation/${obsId}/relate`, { method: 'POST', headers, body: JSON.stringify({ action, target_observation_id: target }) });
            await load(); onChanged && onChanged();
        } catch (e) { /* */ }
        setBusy(null);
    };

    // Part 7: false-positive tag (learning-only).
    const markFP = async (obsId, fp_type) => {
        if (!fp_type) return;
        setBusy('fp' + obsId);
        try {
            await fetch(API + `/api/admin/learning/observation/${obsId}/false-positive`, { method: 'POST', headers, body: JSON.stringify({ fp_type }) });
            await load();
        } catch (e) { /* */ }
        setBusy(null);
    };

    // Knowledge Triage — re-run triage against the current corpus.
    const retriage = async () => {
        if (!data?.job) return;
        setBusy('retriage');
        try {
            await fetch(API + '/api/admin/learning/job/' + data.job.id + '/triage', { method: 'POST', headers });
            await load();
        } catch (e) { /* */ }
        setBusy(null);
    };

    const reanalyze = async () => {
        if (!data?.job) return;
        setBusy('reanalyze');
        try {
            const res = await fetch(API + '/api/admin/learning/reanalyze/' + data.job.audit_id, { method: 'POST', headers });
            const out = await res.json();
            onChanged && onChanged();
            if (out.job?.id) setSelectedJobId(out.job.id);  // jump to the new version
        } catch (e) { /* */ }
        setBusy(null);
    };

    const job = data?.job;
    const isCarrier = job?.source_type === 'carrier_document' || !!data?.carrier;
    // Stable identity: only recompute when data.observations actually changes, so
    // the useMemo below doesn't see a fresh [] dependency on every render.
    const observations = useMemo(() => data?.observations || [], [data?.observations]);
    // Knowledge Triage — label distribution + active-filter view.
    const triageCounts = useMemo(() => {
        const c = {};
        observations.forEach(o => { if (o.triage_label) c[o.triage_label] = (c[o.triage_label] || 0) + 1; });
        return c;
    }, [observations]);
    const visibleObs = useMemo(() => (
        triageFilter === 'all' ? observations : observations.filter(o => o.triage_label === triageFilter)
    ), [observations, triageFilter]);
    const grouped = useMemo(() => {
        const g = {};
        visibleObs.forEach(o => { (g[o.observation_type] = g[o.observation_type] || []).push(o); });
        return g;
    }, [visibleObs]);

    return (
        <div className="le-drawer-overlay" onClick={onClose}>
            <div className="le-drawer" onClick={e => e.stopPropagation()}>
                <div className="le-drawer-head">
                    <button className="history-back-link" onClick={onClose}>✕ Close</button>
                    {job && observations.length > 0 && <button className="history-back-link" onClick={retriage} disabled={busy === 'retriage'}>{busy === 'retriage' ? 'Re-triaging…' : '⚖ Re-triage'}</button>}
                    {job && !isCarrier && <button className="history-back-link" onClick={reanalyze} disabled={busy === 'reanalyze'}>{busy === 'reanalyze' ? 'Re-analyzing…' : '↻ Re-analyze (new version)'}</button>}
                </div>
                {loading && <div className="history-loading"><div className="spinner" /></div>}
                {data?.error && <div className="le-error">⚠ {data.error}</div>}

                {!loading && job && (
                    <div className="le-drawer-body">
                        {/* Estimate information + traceability (Part 5 / Part 16) */}
                        <div className="le-drawer-title">{job.job_name || 'Estimate'} <span className="le-vtag">{isCarrier ? `seq ${data.carrier?.version_seq}` : `v${job.learning_version}`}</span></div>
                        {isCarrier ? (
                            <>
                                <div className="le-meta-grid">
                                    <div><span>Carrier</span><b>{data.carrier?.carrier || '—'}</b></div>
                                    <div><span>Document Type</span><b>{data.carrier?.document_type || '—'}</b></div>
                                    <div><span>Version</span><b>{data.carrier?.version ? `v${data.carrier.version}` : `seq ${data.carrier?.version_seq}`}</b></div>
                                    <div><span>Revision Date</span><b>{data.carrier?.revision_date ? fmtDate(data.carrier.revision_date) : '—'}</b></div>
                                    <div><span>Region</span><b>{data.carrier?.region || '—'}</b></div>
                                    <div><span>Classification</span><b>{fmtPct(data.carrier?.classification_confidence)}</b></div>
                                    <div><span>Pages</span><b>{fmtNum(data.carrier?.page_count)}</b></div>
                                    <div><span>Status</span><b style={{ color: STATUS_COLOR[job.status] }}>{job.status}</b></div>
                                    <div><span>Model</span><b>{job.model || '—'} {job.model_version || ''}</b></div>
                                </div>
                                {data.carrier?.issuer === 'SERVPRO' && (
                                    <div className="le-meta-grid" style={{ marginTop: 8 }}>
                                        <div><span>Issuer</span><b>{data.carrier.issuer}</b></div>
                                        <div><span>Account / Client</span><b>{data.carrier.account_client || '—'}</b></div>
                                        <div><span>Carrier (if insurer)</span><b>{data.carrier.account_is_insurer ? (data.carrier.carrier || '—') : '— (commercial)'}</b></div>
                                        <div><span>Account Scope</span><b>{data.carrier.account_scope || '—'}</b></div>
                                        <div><span>Program Scope</span><b>{data.carrier.program_scope || '—'}</b></div>
                                        <div><span>Bulletin Code</span><b>{data.carrier.bulletin_code || '—'}</b></div>
                                        <div><span>Revision</span><b>{data.carrier.revision != null ? data.carrier.revision : '—'}</b></div>
                                        <div><span>Program Status</span><b>{data.carrier.program_status || '—'}</b></div>
                                        <div><span>Lineage Key</span><b>{data.carrier.lineage_key || '—'}</b></div>
                                        <div><span>Service Lines</span><b>{Array.isArray(data.carrier.service_lines) && data.carrier.service_lines.length ? data.carrier.service_lines.join(', ') : '—'}</b></div>
                                    </div>
                                )}
                                <div className="le-trace">
                                    Traceability: carrier doc&nbsp;{data.carrier?.id?.slice(0, 8)}… · {data.carrier?.original_filename || 'carrier.pdf'}
                                    {data.carrier?.content_sha256 && <> · sha {data.carrier.content_sha256.slice(0, 10)}…</>}
                                    {data.carrier?.storage_path ? <> · PDF stored ✓</> : <> · PDF metadata retained</>}
                                    · uploaded {fmtDate(data.carrier?.created_at)}
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Part 1: full estimate metadata + audit summary */}
                                <div className="le-meta-grid">
                                    <div><span>Company</span><b>{job.company_name || data.customer?.company_name || '—'}</b></div>
                                    <div><span>Contact</span><b>{data.customer?.contact_name || '—'}</b></div>
                                    <div><span>Upload Date</span><b>{fmtDate(data.audit?.created_at || job.created_at)}</b></div>
                                    <div><span>Estimate Type</span><b>{job.estimate_type || job.document_type || '—'}</b></div>
                                    <div><span>Loss Type</span><b>{job.loss_type || '—'}</b></div>
                                    <div><span>Carrier</span><b>{job.carrier || '—'}</b></div>
                                    <div><span>Estimate Size</span><b>{fmt$(job.grand_total)}</b></div>
                                    <div><span>Findings</span><b>{fmtNum(job.findings_count)}</b></div>
                                    <div><span>Opportunity $</span><b>{job.opportunity_total != null ? fmt$(job.opportunity_total) : (data.audit?.dollar_impact != null ? fmt$(data.audit.dollar_impact) : '—')}</b></div>
                                    <div><span>Clean Estimate</span><b>{job.clean_estimate == null ? '—' : (job.clean_estimate ? 'Yes' : 'No')}</b></div>
                                    <div><span>Status</span><b style={{ color: STATUS_COLOR[job.status] }}>{job.status}</b></div>
                                    <div><span>Model</span><b>{job.model || '—'} {job.model_version || ''}</b></div>
                                </div>
                                {/* Audit summary line (severity mix from the saved audit) */}
                                {data.audit && (
                                    <div className="le-trace">
                                        Audit summary: {fmtNum(data.audit.findings_count)} finding(s)
                                        {data.audit.dollar_impact != null && <> · {fmt$(data.audit.dollar_impact)} opportunity</>}
                                        {' '}· {job.clean_estimate ? 'clean estimate ✓' : 'findings present'}
                                    </div>
                                )}
                                <div className="le-trace">
                                    Traceability: audit&nbsp;#{job.audit_id}
                                    {data.audit && <> · uploaded {fmtDate(data.audit.created_at)} · {data.audit.file_name || 'estimate.pdf'}</>}
                                    {data.corpus && <> · corpus {data.corpus.ingestion_status}</>}
                                </div>
                            </>
                        )}

                        {/* Versions — estimate: re-analysis versions (Part 17); carrier: document version lineage (Part 4) */}
                        {data.versions && data.versions.length > 1 && (
                            <div className="le-versions">
                                <div className="gd-section-title">{isCarrier ? 'Document Versions' : 'Learning Versions'}</div>
                                {data.versions.map(v => (
                                    <button key={v.id || v.carrier_document_id} className={'le-version-chip ' + (v.id === job.id ? 'active' : '')}
                                        disabled={!v.id} onClick={() => v.id && setSelectedJobId(v.id)}>
                                        {isCarrier ? `seq ${v.learning_version}` : `v${v.learning_version}`}
                                        {v.version ? ` (v${v.version})` : ''} · {v.observation_count ?? '—'} obs · {fmtDate(v.created_at)}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Observations grouped by type (Parts 2 / 3 / 5 / 6 / 7) */}
                        <div className="gd-section-title">Observations ({observations.length})</div>
                        {observations.length === 0 && <div className="le-empty">No observations generated for this estimate.</div>}

                        {/* Knowledge Triage — AI-assigned labels. AI triages; humans approve. */}
                        {observations.length > 0 && Object.keys(triageCounts).length > 0 && (
                            <div className="le-triage-bar">
                                <span className="le-dim">Triage:</span>
                                <button className={'le-triage-chip ' + (triageFilter === 'all' ? 'active' : '')} onClick={() => setTriageFilter('all')}>All ({observations.length})</button>
                                {TRIAGE_LABELS.filter(l => triageCounts[l]).map(l => (
                                    <button key={l} className={'le-triage-chip ' + (triageFilter === l ? 'active' : '')}
                                        style={{ borderColor: TRIAGE_COLOR[l], color: triageFilter === l ? '#fff' : TRIAGE_COLOR[l], background: triageFilter === l ? TRIAGE_COLOR[l] : 'transparent' }}
                                        onClick={() => setTriageFilter(l)}>{l} ({triageCounts[l]})</button>
                                ))}
                                <span className="le-dim le-triage-note">AI triages · humans approve · nothing auto-promoted</span>
                            </div>
                        )}

                        {/* Part 3: bulk review bar — appears when observations are selected. */}
                        {selected.size > 0 && (
                            <div className="le-bulk-bar">
                                <span className="le-count">{selected.size} selected</span>
                                <button className="le-btn le-approve" disabled={busy} onClick={() => bulkReview('Approve')}>Approve</button>
                                <button className="le-btn le-reject" disabled={busy} onClick={() => bulkReview('Reject')}>Reject</button>
                                <button className="le-btn le-investigate" disabled={busy} onClick={() => bulkReview('Needs Investigation')}>Needs Investigation</button>
                                <button className="le-btn le-triage" disabled={busy} onClick={() => bulkReview('Duplicate')}>Mark Duplicate</button>
                                <button className="le-btn le-triage" disabled={busy} onClick={() => bulkReview('Already Exists')}>Mark Already Exists</button>
                                <button className="le-btn" disabled={busy} onClick={() => setSelected(new Set())}>Clear</button>
                            </div>
                        )}

                        {Object.entries(grouped).map(([type, list]) => (
                            <div key={type} className="le-obs-group">
                                <div className="le-obs-group-title">{type} ({list.length})</div>
                                {list.map(o => (
                                    <div key={o.id} className="le-obs-card">
                                        <div className="le-obs-head">
                                            {o.status === 'Pending' && (
                                                <input type="checkbox" className="le-obs-check" checked={selected.has(o.id)} onChange={() => toggleSel(o.id)} title="Select for bulk review" />
                                            )}
                                            <Badge text={o.confidence_label + ' · ' + fmtPct(o.confidence)} color={CONF_COLOR[o.confidence_label]} />
                                            <Badge text={o.priority} color={PRI_COLOR[o.priority]} />
                                            <Badge text={o.status} color={STATUS_COLOR[o.status]} />
                                            {o.triage_label && <Badge text={'⚖ ' + o.triage_label + (o.triage_confidence != null ? ' · ' + fmtPct(o.triage_confidence) : '')} color={TRIAGE_COLOR[o.triage_label]} />}
                                        </div>
                                        {o.triage_label && (
                                            <div className="le-triage-detail" style={{ borderColor: TRIAGE_COLOR[o.triage_label] }}>
                                                <div className="le-triage-reason"><b>Triage:</b> {o.triage_reason}</div>
                                                {(() => {
                                                    const ev = [];
                                                    const n = (a) => Array.isArray(a) ? a.length : 0;
                                                    if (n(o.matched_knowledge_ids)) ev.push(`${n(o.matched_knowledge_ids)} approved knowledge entr${n(o.matched_knowledge_ids) === 1 ? 'y' : 'ies'}`);
                                                    if (n(o.matched_knowledge_object_ids)) ev.push(`${n(o.matched_knowledge_object_ids)} Knowledge Object(s)`);
                                                    if (n(o.matched_consensus_group_ids)) ev.push(`${n(o.matched_consensus_group_ids)} Consensus Group(s)`);
                                                    if (n(o.matched_recommendation_ids)) ev.push(`${n(o.matched_recommendation_ids)} Recommendation(s)`);
                                                    if (n(o.duplicate_observation_ids)) ev.push(`${n(o.duplicate_observation_ids)} prior observation(s)`);
                                                    return ev.length ? <div className="le-triage-matched"><span className="le-dim">Matched evidence:</span> {ev.join(' · ')}</div> : null;
                                                })()}
                                            </div>
                                        )}
                                        <div className="le-obs-reason">{o.reasoning}</div>
                                        {o.suggested_action && <div className="le-obs-action"><b>Suggested:</b> {o.suggested_action}</div>}
                                        {o.supporting_evidence && <Evidence label="Evidence" data={o.supporting_evidence} />}
                                        {o.supporting_line_items && <Evidence label="Line Items" data={o.supporting_line_items} />}
                                        {o.supporting_rooms && <Evidence label="Rooms" data={o.supporting_rooms} />}
                                        {o.supporting_findings && <Evidence label="Findings" data={o.supporting_findings} />}

                                        {/* Part 5: promotion preview ("what this will create") */}
                                        {previews[o.id] && (
                                            <div className="le-preview">
                                                <div className="le-preview-title">What approving this will create</div>
                                                <ul>
                                                    {previews[o.id].willCreate?.map((c, i) => <li key={i}><b>{c.artifact}</b> — {c.detail}</li>)}
                                                </ul>
                                                <div className="le-preview-sub">May later strengthen: {previews[o.id].mayStrengthen?.map(m => m.artifact).join(' · ')}</div>
                                                <div className="le-preview-note">{previews[o.id].note}</div>
                                            </div>
                                        )}

                                        {/* Part 6: duplicate detection panel */}
                                        {dups[o.id] && (
                                            <div className="le-dups">
                                                <div className="le-preview-title">Possible duplicates</div>
                                                {(!dups[o.id].similarObservations?.length && !dups[o.id].knowledge?.length && !dups[o.id].recommendations?.length) && (
                                                    <div className="le-empty">No similar observations, knowledge entries, or recommendations found.</div>
                                                )}
                                                {dups[o.id].similarObservations?.map(s => (
                                                    <div key={s.id} className="le-dup-row">
                                                        <span>{fmtPct(s.similarity)} match · audit #{s.audit_id} · <Badge text={s.status} color={STATUS_COLOR[s.status]} /></span>
                                                        {o.status === 'Pending' && (
                                                            <span className="le-dup-acts">
                                                                <button className="le-btn le-triage" disabled={busy} onClick={() => relate(o.id, 'Merge', s.id)}>Merge</button>
                                                                <button className="le-btn" disabled={busy} onClick={() => relate(o.id, 'Link', s.id)}>Link</button>
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                                {dups[o.id].knowledge?.map(k => <div key={k.id} className="le-dup-row le-dim">Existing Knowledge: {k.category} · {k.title}</div>)}
                                                {dups[o.id].recommendations?.map(r => <div key={r.id} className="le-dup-row le-dim">Existing Recommendation: {r.title}</div>)}
                                                {o.status === 'Pending' && <button className="le-btn" disabled={busy} onClick={() => relate(o.id, 'Keep separate')}>Keep separate</button>}
                                            </div>
                                        )}

                                        {o.status === 'Pending' ? (
                                            <>
                                                {/* Part 2: full quick-review vocabulary */}
                                                <div className="le-obs-actions">
                                                    {REVIEW_ACTIONS.map(a => (
                                                        <button key={a.action} className={'le-btn ' + a.cls} disabled={busy} onClick={() => review(o.id, a.action)}>{a.action}</button>
                                                    ))}
                                                </div>
                                                <div className="le-obs-actions le-obs-tools">
                                                    <button className="le-btn" disabled={busy} onClick={() => loadPreview(o.id)}>Preview promotion</button>
                                                    <button className="le-btn" disabled={busy} onClick={() => loadDups(o.id)}>Find duplicates</button>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="le-obs-reviewed">
                                                Reviewed: {o.status}{o.reviewed_at ? ` · ${fmtDateTime(o.reviewed_at)}` : ''}
                                                {o.review_time_ms != null && ` · ${Math.round(o.review_time_ms / 1000)}s`}
                                                {o.backlog_id && ' · → backlog'}{o.knowledge_id && ' · → knowledge base'}
                                                {o.duplicate_of_observation_id && ' · merged duplicate'}
                                            </div>
                                        )}

                                        {/* Part 7: false-positive tagging (learning-only; never changes production) */}
                                        <div className="le-fp-row">
                                            <span className="le-dim">Tag as false positive:</span>
                                            {FP_TYPES.map(t => (
                                                <button key={t} className="le-btn le-fp" disabled={busy} onClick={() => markFP(o.id, t)}>{t}</button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Evidence({ label, data }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="le-evidence">
            <button className="le-evidence-toggle" onClick={() => setOpen(o => !o)}>{open ? '▾' : '▸'} {label}</button>
            {open && <pre className="le-evidence-pre">{JSON.stringify(data, null, 2)}</pre>}
        </div>
    );
}
